import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, normalizeStyle as _normalizeStyle, isRef as _isRef, withCtx as _withCtx, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "w-full" }
const _hoisted_2 = { class: "flex justify-content-between items-center p-2" }
const _hoisted_3 = { class: "col flex items-center" }
const _hoisted_4 = {
  key: 0,
  class: "col-fixed flex items-center"
}
const _hoisted_5 = { class: "line-class my-auto mr-2 p-2 flex justify-content-end" }
const _hoisted_6 = {
  key: 1,
  class: "col-fixed flex items-center"
}
const _hoisted_7 = ["onClick", "textContent"]
const _hoisted_8 = ["textContent"]
const _hoisted_9 = ["textContent"]
const _hoisted_10 = ["textContent"]
const _hoisted_11 = ["textContent"]
const _hoisted_12 = ["textContent"]
const _hoisted_13 = ["textContent"]
const _hoisted_14 = { class: "ml-3" }
const _hoisted_15 = {
  key: 1,
  class: "ml-4"
}

import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { TreeNode } from 'primevue/treenode';
import Calendar from 'primevue/calendar';
import OverlayPanel from 'primevue/overlaypanel';
import TreeTable, { TreeTableSelectionKeys, TreeTableExpandedKeys } from 'primevue/treetable';
import RadioButton from 'primevue/radiobutton';
import AppTabMenu from '@/components/AppTabMenu';
import OrganizationContractModal from './components/OrganizationContractModal.vue';
import { useDateTime, can } from '@/composables';
import { useOrganizationContractsStore } from '@/store';
import { ContractDetailInterface, DisciplineInterface } from '@/types';
import { PERMISSIONS } from '@/enum';


export default /*@__PURE__*/_defineComponent({
  __name: 'OrganizationContracts',
  setup(__props) {

const displayModal = ref<boolean>(false);
const selectedContract = ref<ContractDetailInterface>({} as ContractDetailInterface);
const selectedKey = ref<TreeTableSelectionKeys>([]);
const expandedKeys = ref<TreeTableExpandedKeys>([]);
const contractStore = useOrganizationContractsStore();
const { FILTER, scheduleScrub, minScheduleScrubDate, maxScheduleScrubDate, isScrubbed } = storeToRefs(contractStore);
const selectedFilterIndex = ref<number>(0);
const canEdit = contractStore.canEditOrCreateContracts;
const calendar = ref();

const disableCalendarScheduleScrub = computed(() => can([PERMISSIONS.CONTRACTS_SHOW_SCHEDULE_SCRUB_DATE]));
const canEditScheduleScrub = computed(() => {
  return can([PERMISSIONS.CONTRACTS_POSTPONE_SCHEDULE_SCRUB_DATE]) && scheduleScrub.value && !isScrubbed.value;
});

const minDate = computed(() => {
  const minDate = new Date(minScheduleScrubDate.value);
  minDate.setHours(0, 0, 0, 0);

  return minDate;
});

const maxDate = computed(() => {
  const maxDate = new Date(maxScheduleScrubDate.value);
  maxDate.setHours(0, 0, 0, 0);

  return maxDate;
});

const scheduleScrubDate = computed(() => {
  if (!scheduleScrub.value) return '';
  const date = new Date(scheduleScrub.value);

  return date.toLocaleDateString('en-US', { timeZone: 'UTC' });
});

/**
 * The available reports that should be displayed within the tabs.
 */
const filters = Object.values(FILTER.value).map((filter) => ({ label: filter }));

const contracts = ref<TreeNode[]>([]);

onMounted(async () => {
  await getScheduleScrub();
  await getContracts();
});

const getScheduleScrub = async () => {
  await contractStore.getScheduledScrub();
};

const getContracts = async () => {
  await contractStore.getContracts();
  contracts.value = contractStore.contractsTableData;
};

const showContract = async (id: number) => {
  selectedContract.value = contractStore.contracts.find((contract) => contract.id == id) as ContractDetailInterface;

  if (selectedContract.value.id) {
    const id = selectedContract.value.id;
    await contractStore.showContract(id);
    toggleModal();
  }
};

const contractExpansion = async (id: number) => {
  selectedContract.value = contractStore.contracts.find((contract) => contract.id == id) as ContractDetailInterface;
  contractStore.expandOrganizationContract(selectedContract.value.id as number);
  toggleModal();
};

const tabChange = (e: number) => {
  selectedFilterIndex.value = e;
  contractStore.filterContracts(filters[e].label);
  contracts.value = contractStore.contractsTableData;
};

const nameWithComma = (arr: DisciplineInterface[]) =>
  arr.map((item, index) => (index !== arr.length - 1 ? `${item.discipline?.code}, ` : item.discipline?.code)).join('');

const formatModel = (text: string) => {
  return text
    .toLowerCase()
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ');
};

const onRenewOrganizationContracts = async () => {
  await contractStore.renewOrganizationContracts(selectedContract.value.id as number);
  toggleModal();
};

const onCreateOrganizationContracts = async () => {
  selectedContract.value = await contractStore.getNewContractTemplate();
  toggleModal();
};

const onSave = async () => {
  toggleModal();
  await getContracts();
  await getScheduleScrub();
  tabChange(selectedFilterIndex.value);
};

const contractIsActive = (contract: ContractDetailInterface) => {
  const today = new Date();
  if (new Date(contract.start_at) < today && new Date(contract.end_at) > today) return true;
  return false;
};

const contractIsFuture = (contract: ContractDetailInterface) => {
  const today = new Date();
  if (new Date(contract.start_at) > today && new Date(contract.end_at) > today) return true;
  return false;
};

const selectionChanged = (selectionObject: TreeTableSelectionKeys) => {
  selectedKey.value = selectionObject;
  const id = Object.keys(selectionObject)[0];
  if (id) {
    selectedContract.value = contractStore.contracts.find(
      (contract) => contract?.id?.toString() == id.toString(),
    ) as ContractDetailInterface;
  }
};

const postponeScheduleScrub = async () => {
  const date = useDateTime().formatDateForApi(scheduleScrub.value);
  await contractStore
    .postponeScheduleScrub(date)
    .then(() => {
      contractStore.doToast('Schedule scrub postponed', 'Success');
    })
    .catch((e) => {
      const message = e.data?.message || 'Failed to postpone schedule scrub';
      contractStore.doToast(message, 'Error', true);
    });

  await getScheduleScrub();
  calendar.value.hide();
};

const viewCalendar = (event: Event) => {
  if (canEditScheduleScrub.value) {
    calendar.value.show(event);
  }
};

const toggleModal = () => {
  displayModal.value = !displayModal.value;
};

return (_ctx: any,_cache: any) => {
  const _component_InputText = _resolveComponent("InputText")!
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_Column = _resolveComponent("Column")!
  const _component_Icon = _resolveComponent("Icon")!
  const _component_Card = _resolveComponent("Card")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_Card, null, {
      content: _withCtx(() => [
        (displayModal.value)
          ? (_openBlock(), _createBlock(OrganizationContractModal, {
              key: 0,
              id: selectedContract.value.id,
              onCancel: toggleModal,
              onSave: onSave
            }, null, 8, ["id"]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_unref(AppTabMenu), {
              "class-name": "filter-menu-container",
              items: _unref(filters),
              onTab: tabChange
            }, null, 8, ["items"])
          ]),
          (disableCalendarScheduleScrub.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _cache[4] || (_cache[4] = _createElementVNode("p", { class: "text-dark font-semibold my-auto mr-2" }, "Data Deleted:", -1)),
                _createVNode(_component_InputText, {
                  modelValue: scheduleScrubDate.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((scheduleScrubDate).value = $event)),
                  "input-style": "width: 20%; margin-left: 5px;",
                  style: _normalizeStyle({ color: _unref(isScrubbed) ? 'gray' : 'black' }),
                  disabled: _unref(isScrubbed) || !_unref(scheduleScrub),
                  readonly: "",
                  onClick: viewCalendar
                }, null, 8, ["modelValue", "style", "disabled"]),
                (canEditScheduleScrub.value)
                  ? (_openBlock(), _createBlock(_unref(OverlayPanel), {
                      key: 0,
                      ref_key: "calendar",
                      ref: calendar
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Calendar), {
                          modelValue: _unref(scheduleScrub),
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(scheduleScrub) ? (scheduleScrub).value = $event : null)),
                          "min-date": minDate.value,
                          "max-date": maxDate.value,
                          format: "MM/dd/yyyy",
                          inline: true
                        }, {
                          footer: _withCtx(() => [
                            _createElementVNode("div", _hoisted_5, [
                              _createVNode(_component_AppButton, {
                                type: "secondary",
                                text: "Postpone",
                                onClick: postponeScheduleScrub
                              })
                            ])
                          ]),
                          _: 1
                        }, 8, ["modelValue", "min-date", "max-date"])
                      ]),
                      _: 1
                    }, 512))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          (_unref(canEdit))
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                (_unref(contractStore).contracts.length)
                  ? (_openBlock(), _createBlock(_component_AppButton, {
                      key: 0,
                      type: "secondary",
                      text: "Renewal",
                      "icon-left": "add",
                      class: "ml-2",
                      disabled: !selectedContract.value?.id,
                      onClick: onRenewOrganizationContracts
                    }, null, 8, ["disabled"]))
                  : (_openBlock(), _createBlock(_component_AppButton, {
                      key: 1,
                      type: "secondary",
                      text: "Create New Contract",
                      class: "ml-2",
                      onClick: onCreateOrganizationContracts
                    }))
              ]))
            : _createCommentVNode("", true)
        ]),
        (contracts.value.length)
          ? (_openBlock(), _createBlock(_unref(TreeTable), {
              key: 1,
              "expanded-keys": expandedKeys.value,
              "onUpdate:expandedKeys": _cache[3] || (_cache[3] = ($event: any) => ((expandedKeys).value = $event)),
              value: contracts.value,
              "data-key": "key",
              "loading-icon": "logo-spinner",
              loading: _unref(contractStore).loading,
              "responsive-layout": "scroll",
              "selection-mode": _unref(canEdit) ? 'single' : undefined
            }, {
              empty: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                _createTextVNode("No contracts found", -1)
              ]))]),
              default: _withCtx(() => [
                (contracts.value.length)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createVNode(_component_Column, {
                        expander: "",
                        class: "w-2rem col-fixed"
                      }),
                      (_unref(canEdit))
                        ? (_openBlock(), _createBlock(_component_Column, {
                            key: 0,
                            "body-style": "justify-content: center",
                            "header-class": "text-align-center",
                            class: "col-fixed"
                          }, {
                            body: _withCtx(({ node }) => [
                              _createVNode(_unref(RadioButton), {
                                modelValue: selectedKey.value,
                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedKey).value = $event)),
                                value: { [node.data.id]: true },
                                onClick: ($event: any) => (selectionChanged({ [node.data.id]: true }))
                              }, null, 8, ["modelValue", "value", "onClick"])
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true),
                      _createVNode(_component_Column, {
                        field: "type",
                        header: "Type",
                        "header-class": "text-align-center"
                      }, {
                        body: _withCtx(({ node }) => [
                          _createElementVNode("p", {
                            class: "text-primary font-semibold cursor-pointer text-underline",
                            onClick: ($event: any) => (showContract(node.data.id)),
                            textContent: _toDisplayString(node.data.contract_type.type)
                          }, null, 8, _hoisted_7)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_Column, {
                        field: "start_date",
                        header: "Start Date",
                        "header-class": "text-center",
                        class: "pointer-events-none"
                      }, {
                        body: _withCtx(({ node }) => [
                          _createElementVNode("span", {
                            class: "flex justify-content-center",
                            textContent: _toDisplayString(_unref(useDateTime)().formatIntlDate(node.data.start_at))
                          }, null, 8, _hoisted_8)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_Column, {
                        field: "end_date",
                        header: "End Date",
                        "header-class": "text-center",
                        class: "pointer-events-none"
                      }, {
                        body: _withCtx(({ node }) => [
                          _createElementVNode("span", {
                            class: "flex justify-content-center",
                            textContent: _toDisplayString(_unref(useDateTime)().formatIntlDate(node.data.end_at))
                          }, null, 8, _hoisted_9)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_Column, {
                        field: "model",
                        header: "Model",
                        "header-class": "text-center",
                        class: "pointer-events-none"
                      }, {
                        body: _withCtx(({ node }) => [
                          _createElementVNode("span", {
                            class: "flex justify-content-center",
                            textContent: _toDisplayString(formatModel(node.data.disciplines[0].contract_type))
                          }, null, 8, _hoisted_10)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_Column, {
                        field: "disciplines",
                        header: "Disciplines",
                        "header-class": "text-center",
                        class: "pointer-events-none"
                      }, {
                        body: _withCtx(({ node }) => [
                          _createElementVNode("span", {
                            class: "flex justify-content-center",
                            textContent: _toDisplayString(nameWithComma(node.data.disciplines))
                          }, null, 8, _hoisted_11)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_Column, {
                        field: "salesrep",
                        header: "Sales Rep",
                        "header-class": "text-center",
                        class: "pointer-events-none"
                      }, {
                        body: _withCtx(({ node }) => [
                          _createElementVNode("span", {
                            class: "flex justify-content-center",
                            textContent: _toDisplayString(node.data.salesrep?.display_name)
                          }, null, 8, _hoisted_12)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_Column, {
                        field: "quote_number",
                        header: "Quote #",
                        "header-class": "text-center",
                        class: "pointer-events-none"
                      }, {
                        body: _withCtx(({ node }) => [
                          _createElementVNode("span", {
                            class: "flex justify-content-center",
                            textContent: _toDisplayString(node.data.quote_number)
                          }, null, 8, _hoisted_13)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_Column, {
                        field: "prepay",
                        header: "Prepay",
                        "header-class": "text-center",
                        class: "pointer-events-none"
                      }, {
                        body: _withCtx(({ node }) => [
                          _createElementVNode("div", _hoisted_14, [
                            (node.data.prepay !== 0)
                              ? (_openBlock(), _createBlock(_component_Icon, {
                                  key: 0,
                                  class: "text-teal-600",
                                  name: "check"
                                }))
                              : (_openBlock(), _createBlock(_component_Icon, {
                                  key: 1,
                                  class: "text-red-600",
                                  name: "close"
                                }))
                          ])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_Column, {
                        field: "actions",
                        header: "Actions",
                        "header-class": "text-center"
                      }, {
                        body: _withCtx(({ node }) => [
                          ((contractIsActive(node.data) || contractIsFuture(node.data)) && _unref(canEdit))
                            ? (_openBlock(), _createBlock(_component_AppButton, {
                                key: 0,
                                class: "w-full justify-content-center",
                                "full-width": true,
                                type: "text",
                                size: "medium",
                                text: "Expansion",
                                "icon-left": "add",
                                onClick: ($event: any) => (contractExpansion(node.data.id))
                              }, null, 8, ["onClick"]))
                            : (_openBlock(), _createElementBlock("div", _hoisted_15, "-"))
                        ]),
                        _: 1
                      })
                    ], 64))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["expanded-keys", "value", "loading", "selection-mode"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})